import type { AppSettings } from "../utils/types.ts";

export const DEFAULT_SETTINGS: AppSettings = {
  privacyMode: "maximum-safety",
  concealmentType: "both",
  concealmentStrength: 8,
  cameraPanelSize: "medium",
  cameraPanelPosition: "bottom-right",
  particleQuality: "auto",
  colourTheme: "nebula",
  gestureSensitivity: 6,
  gestureSmoothing: 7,
  soundEnabled: false,
  volume: 60,
  showFps: false,
  debugMode: false,
  presentationMode: false,
  mirrorCamera: true,
};

const PRIVACY_MODES = new Set(["maximum-safety", "face-mask"]);
const CONCEALMENT = new Set(["pixelate", "blur", "both"]);
const QUALITY = new Set(["low", "medium", "high", "auto"]);
const THEMES = new Set(["nebula", "aurora", "ember", "mono"]);
const SIZES = new Set(["small", "medium", "large"]);
const POSITIONS = new Set(["bottom-right", "bottom-left", "top-right", "top-left"]);

function asNumber(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function validateSettings(input: unknown): AppSettings {
  const raw = input && typeof input === "object" ? (input as Record<string, unknown>) : {};
  const privacyMode = PRIVACY_MODES.has(String(raw.privacyMode))
    ? (raw.privacyMode as AppSettings["privacyMode"])
    : DEFAULT_SETTINGS.privacyMode;
  const concealmentType = CONCEALMENT.has(String(raw.concealmentType))
    ? (raw.concealmentType as AppSettings["concealmentType"])
    : DEFAULT_SETTINGS.concealmentType;
  const particleQuality = QUALITY.has(String(raw.particleQuality))
    ? (raw.particleQuality as AppSettings["particleQuality"])
    : DEFAULT_SETTINGS.particleQuality;
  const colourTheme = THEMES.has(String(raw.colourTheme))
    ? (raw.colourTheme as AppSettings["colourTheme"])
    : DEFAULT_SETTINGS.colourTheme;
  const cameraPanelSize = SIZES.has(String(raw.cameraPanelSize))
    ? (raw.cameraPanelSize as AppSettings["cameraPanelSize"])
    : DEFAULT_SETTINGS.cameraPanelSize;
  const cameraPanelPosition = POSITIONS.has(String(raw.cameraPanelPosition))
    ? (raw.cameraPanelPosition as AppSettings["cameraPanelPosition"])
    : DEFAULT_SETTINGS.cameraPanelPosition;

  return {
    privacyMode,
    concealmentType,
    concealmentStrength: asNumber(raw.concealmentStrength, 8, 1, 10),
    cameraPanelSize,
    cameraPanelPosition,
    particleQuality,
    colourTheme,
    gestureSensitivity: asNumber(raw.gestureSensitivity, 6, 1, 10),
    gestureSmoothing: asNumber(raw.gestureSmoothing, 7, 1, 10),
    soundEnabled: asBool(raw.soundEnabled, false),
    volume: asNumber(raw.volume, 60, 0, 100),
    showFps: asBool(raw.showFps, false),
    debugMode: asBool(raw.debugMode, false),
    presentationMode: asBool(raw.presentationMode, false),
    mirrorCamera: asBool(raw.mirrorCamera, true),
  };
}
